"use strict";

import Swal from "sweetalert2";

/**
 * Build HTML for the import error popup.
 * Keep it pure: only use inputs, return string.
 */
export const buildImportErrorHtml = ({ message, total, imported, failed, errors }) => {
    // Group by row + question
    const grouped = new Map();

    (errors || []).forEach((e) => {
        const key = `${e.row}__${e.question || ""}`;
        if (!grouped.has(key)) grouped.set(key, { row: e.row, question: e.question, items: [] });
        grouped.get(key).items.push(e.error);
    });

    const groups = Array.from(grouped.values()).sort((a, b) => a.row - b.row);

    const safeMsg = message || "Fix the highlighted questions and re-upload.";

    let html = `
      <div style="
        font-family: var(--globals-font-family, 'Segoe UI');
      ">
        <!-- Header -->
        <div style="
          margin-bottom: 20px;
        ">
          <div style="
            font-size: var(--typography-font-size-2xl-20px, 20px);
            font-weight: var(--typography-font-weights-semibold, 600);
            color: var(--colors-text-primary, #1d1d1d);
            margin: 0 0 8px 0;
            line-height: var(--typography-line-heights-2, 110%);
          ">
            Import Failed
          </div>
          <div style="
            font-size: var(--typography-font-size-lg-14px, 14px);
            color: var(--colors-text-secondary, #6c757d);
            margin: 0;
            line-height: var(--typography-line-heights-2, 110%);
          ">
            ${safeMsg}
          </div>
        </div>

        <!-- Summary Cards -->
        <div style="
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 24px;
        ">
          <!-- Total Card -->
          <div style="
            border-radius: var(--globals-border-radius, 10px);
            padding: 12px;
            text-align: center;
            background: var(--colors-neutral-grey-100, #f8f8fc);
            border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
          ">
            <div style="
              font-size: var(--typography-font-size-md-12px, 12px);
              color: var(--colors-text-secondary, #6c757d);
              margin-bottom: 6px;
              line-height: var(--typography-line-heights-1, 100%);
            ">
              Total
            </div>
            <div style="
              font-size: var(--typography-font-size-2xl-20px, 20px);
              font-weight: var(--typography-font-weights-semibold, 600);
              color: var(--colors-text-primary, #1d1d1d);
              line-height: var(--typography-line-heights-1, 100%);
            ">
              ${total ?? 0}
            </div>
          </div>

          <!-- Imported Card -->
          <div style="
            border-radius: var(--globals-border-radius, 10px);
            padding: 12px;
            text-align: center;
            background: var(--colors-neutral-grey-100, #f8f8fc);
            border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
          ">
            <div style="
              font-size: var(--typography-font-size-md-12px, 12px);
              color: var(--colors-text-secondary, #6c757d);
              margin-bottom: 6px;
              line-height: var(--typography-line-heights-1, 100%);
            ">
              Imported
            </div>
            <div style="
              font-size: var(--typography-font-size-2xl-20px, 20px);
              font-weight: var(--typography-font-weights-semibold, 600);
              color: var(--colors-states-success, #2ab554);
              line-height: var(--typography-line-heights-1, 100%);
            ">
              ${imported ?? 0}
            </div>
          </div>

          <!-- Failed Card -->
          <div style="
            border-radius: var(--globals-border-radius, 10px);
            padding: 12px;
            text-align: center;
            background: var(--colors-neutral-grey-100, #f8f8fc);
            border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
          ">
            <div style="
              font-size: var(--typography-font-size-md-12px, 12px);
              color: var(--colors-text-secondary, #6c757d);
              margin-bottom: 6px;
              line-height: var(--typography-line-heights-1, 100%);
            ">
              Failed
            </div>
            <div style="
              font-size: var(--typography-font-size-2xl-20px, 20px);
              font-weight: var(--typography-font-weights-semibold, 600);
              color: var(--colors-states-alert, #c1232c);
              line-height: var(--typography-line-heights-1, 100%);
            ">
              ${failed ?? 0}
            </div>
          </div>
        </div>

        <!-- Error List Section -->
        <div style="
          margin-bottom: 16px;
        ">
          <div style="
            font-size: var(--typography-font-size-lg-14px, 14px);
            font-weight: var(--typography-font-weights-semibold, 600);
            color: var(--colors-text-primary, #1d1d1d);
            margin: 0 0 12px 0;
          ">
            Errors to fix
          </div>

          <div style="
            max-height: 220px;
            overflow-y: auto;
            padding-right: 4px;
          ">
    `;

    // Error Cards
    groups.forEach((g, idx) => {
        html += `
        <div style="
          border-radius: var(--globals-border-radius, 10px);
          border-left: 4px solid var(--colors-states-alert, #c1232c);
          padding: 10px 12px;
          margin-bottom: 8px;
          background: var(--colors-surfaces-container-background, #ffffff);
          border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
          box-shadow: 0 1px 2px var(--colors-surfaces-shadow, #0000001a);
        ">
          <div style="
            font-size: var(--typography-font-size-lg-14px, 14px);
            font-weight: var(--typography-font-weights-semibold, 600);
            color: var(--colors-text-primary, #1d1d1d);
            margin: 0 0 4px 0;
            line-height: var(--typography-line-heights-2, 110%);
          ">
            Row ${g.row}
          </div>
          <div style="
            font-size: var(--typography-font-size-md-12px, 12px);
            color: var(--colors-text-secondary, #6c757d);
            margin: 0 0 6px 0;
            line-height: var(--typography-line-heights-2, 110%);
            font-style: italic;
          ">
            ${g.question || "No question text"}
          </div>
          <ul style="
            margin: 0;
            padding-left: 16px;
          ">
      `;

        g.items.forEach((msg) => {
            html += `
          <li style="
            font-size: var(--typography-font-size-md-12px, 12px);
            color: var(--colors-states-alert, #c1232c);
            margin-bottom: 2px;
            line-height: var(--typography-line-heights-2, 110%);
          ">
            ${msg}
          </li>
        `;
        });

        html += `
          </ul>
        </div>
      `;
    });

    html += `
        </div>
      </div>

      <!-- Action Hint -->
      <div style="
        font-size: var(--typography-font-size-md-12px, 12px);
        color: var(--colors-text-secondary, #6c757d);
        text-align: center;
        padding: 8px;
        background: var(--colors-neutral-grey-100, #f8f8fc);
        border-radius: var(--globals-border-radius, 10px);
        border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
      ">
        Fix the questions listed above, then re-upload the file.
      </div>
    </div>
    `;

    return html;
};

/**
 * Show the SweetAlert popup for import failures.
 */
export const showImportFailurePopup = (payload) => {
    return Swal.fire({
        icon: "error",
        title: "",
        html: buildImportErrorHtml(payload),
        width: "500px",
        confirmButtonText: "Close",
        confirmButtonColor: "var(--colors-theme-button-color, #467fcf)",
        showCloseButton: true,
        customClass: {
            confirmButton: "btn btn-primary d-flex align-items-center",
            closeButton: "btn-close",
            popup: "border-radius: var(--globals-border-radius, 10px);"
        }
    });
};

/**
 * Show the SweetAlert popup for import success.
 */
export const showImportSuccessPopup = ({ total, imported }) => {
    return Swal.fire({
        icon: "success",
        title: "Import Successful",
        html: `
      <div style="
        text-align: center;
        font-family: var(--globals-font-family, 'Segoe UI');
      ">
        <div style="
          font-size: var(--typography-font-size-2xl-20px, 20px);
          font-weight: var(--typography-font-weights-semibold, 600);
          color: var(--colors-states-success, #2ab554);
          margin-bottom: 8px;
        ">
          ${imported} imported
        </div>
        <div style="
          font-size: var(--typography-font-size-md-12px, 12px);
          color: var(--colors-text-secondary, #6c757d);
          margin-bottom: 8px;
        ">
          Total in file: ${total}
        </div>
        <div style="
          font-size: var(--typography-font-size-md-12px, 12px);
          color: var(--colors-states-warning, #f68e31);
          background: var(--colors-neutral-grey-100, #f8f8fc);
          border-radius: var(--globals-border-radius, 10px);
          padding: 10px;
          border: 1px solid var(--colors-surfaces-border-divider, #dee2e6);
          margin-top: 8px;
        ">
          <strong>Note:</strong> Please review imported questions
        </div>
      </div>
    `,
        width: "420px",
        timer: 3500,
        timerProgressBar: true,
        showConfirmButton: false,
        customClass: {
            popup: "border-radius: var(--globals-border-radius, 10px);"
        }
    });
};